import { useEffect, useMemo } from "react";
import Modal from "react-modal";
import { Button } from "@dreambigger/design-system/src/components";
import useScript from "../../api/use-script";
import { useRef } from "react";
import {
  FinancialBrand,
  OnboardingApplication,
} from "@dreambigger/shared/src/types";
import { CameraFilled } from "@ant-design/icons";

export interface VouchedIDVProps {
  showModal: boolean;
  setShowModal: any;
  setVerificationResult: any;
  brand: FinancialBrand;
  deviceHandoff?: boolean;
  config?: any;
  application?: OnboardingApplication;
}

export interface VouchedResponse {
  errors: string[];
  id: string;
  result: {
    success: boolean;
  };
}

type UserDetails = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
};

export default function VouchedIDV({
  showModal,
  setShowModal,
  setVerificationResult,
  brand,
  deviceHandoff,
  config,
  application,
}: VouchedIDVProps) {
  const getUserDetails = (
    application: OnboardingApplication
  ): UserDetails | undefined => {
    if (!application) return;
    const keysToSearchFor = ["firstName", "lastName"];
    // Define empty object to hold data
    let userDetails: any = {};
    // Search through the responses in the application and the available keys in each response.
    // If a key is found, add it along with its value to the userDetails object.
    for (const response of application.responses) {
      const keys = Object.keys(response.fields);
      for (const key of keys) {
        if (keysToSearchFor.includes(key)) {
          userDetails[key] = response.fields[key];
        }
      }
    }
    return userDetails;
  };
  const vouchedScriptReady = useScript(
    "https://static.vouched.id/plugin/releases/latest/index.js"
  );
  const id = "vouched-container";
  const ref = useRef(null);
  const DEFAULT_CONFIG = {
    appId: process.env.NEXT_PUBLIC_VOUCHED_PUBLIC_KEY,
    liveness: "distance",
    id: "camera",
    font: "proxima-nova",
    includeBackId: true,
    content: {
      startCompanyInstructions:
        "Welcome! This process takes just a minute. Please have your government-issued ID handy.",
      progressIndicatorVouching: "verifying",
      cameraButtonLabelContinueSelfie: "Continue",
      cameraTopBackIdDirections:
        "Please turn the ID around and get a picture of the back.",
    },
    userConfirmation: {
      confirmImages: true,
    },
    enableGeoLocation: true,
    theme: {
      logo: {
        src: brand?.primaryLogo,
        style: { "max-width": 150, "margin-bottom": 10 },
      },
      baseColor: brand?.primaryColor,
    },
    crossDevice: deviceHandoff,
    crossDeviceSMS: deviceHandoff,
    crossDeviceShowOff: deviceHandoff,
    crossDeviceQRCode: deviceHandoff,
    showTermsAndPrivacy: true,
    properties: [
      {
        name: "onboarding_application_id",
        value: application && application.id,
      },
    ],
    verification: application && getUserDetails(application),
    callbackURL:
      application &&
      `${process.env.NEXT_PUBLIC_ACQUIRE_API_URL}/onboarding-applications/${application.id}/vouched/job-results`,
    onDone: (job: any) => {
      setVerificationResult(job);
    },
  };

  const instance = useMemo(() => {
    if (!vouchedScriptReady || !(window as any).Vouched || !application) return;
    return (window as any).Vouched({
      ...DEFAULT_CONFIG,
      ...config, // Flow config can override default options
    });
  }, [vouchedScriptReady, application, showModal]);

  // Mount vouched after modal loads.
  useEffect(() => {
    if (!instance) return;
    if (showModal) {
      instance.mount(`#${id}`);
    }
  }, [instance, showModal]);

  return (
    <Modal
      isOpen={showModal}
      className="mh-auto h-100 md_w-70 lg_w-50 xxl_w-25 md_br-4"
      style={{
        content: {
          backgroundColor: "white",
          overflowY: "scroll",
          overflowX: "hidden",
        },
        overlay: {
          zIndex: 999,
          backgroundColor: "rgb(0,0,0,.6)",
        },
      }}
    >
      {/* Modal Header */}
      <div className="flex justify-between items-center bg-primary w-100">
        <h3 className="f-6 mb-0 textPrimary pl-4 fwb usn">
          <CameraFilled /> ID Verification
        </h3>
        <Button
          type="primary"
          className="lift f-6 br-0 pl-3 pr-4 pb-0 h-auto"
          onClick={() => {
            if (instance) {
              instance.unmount(`#${id}`);
            }
            setShowModal(false);
          }}
        >
          X
        </Button>
      </div>
      {/* Modal Content - Vouched */}
      <div className="w-100 h-100">
        {/* This div is targeted by the vouched script. */}
        <div id={id} ref={ref} />
      </div>
    </Modal>
  );
}
